// Age verification gate.
//
// Renders a modal blocking the page until the visitor confirms legal drinking
// age. State persists in the `_age_confirmed` cookie. The cookie is "strictly
// necessary" under ePrivacy (legal-compliance requirement for alcohol sales),
// so the age gate appears before the cookie-consent banner. Once confirmed it
// dispatches an `age-gate:confirmed` event on `document` so dependent popups
// (e.g. the cookie banner) can initialise.
const COOKIE_NAME = '_age_confirmed';
const EVENT_NAME = 'age-gate:confirmed';

function readCookie(name) {
  return document.cookie
    .split('; ')
    .find((c) => c.startsWith(name + '='))
    ?.split('=')[1];
}

function writeCookie(name, value, days) {
  const maxAge = Math.max(1, Number(days) || 30) * 24 * 60 * 60;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; samesite=lax`;
}

export function isAgeConfirmed() {
  return readCookie(COOKIE_NAME) === '1';
}

export function whenAgeConfirmed() {
  if (isAgeConfirmed()) return Promise.resolve();
  if (!document.querySelector('age-gate')) return Promise.resolve();
  return new Promise((resolve) => {
    document.addEventListener(EVENT_NAME, () => resolve(), { once: true });
  });
}

// Expose for non-module consumers (the cookie banner imports the helpers
// directly, but this also lets other scripts feature-detect cheaply.)
window.AgeGate = { isAgeConfirmed, whenAgeConfirmed };

class AgeGate extends HTMLElement {
  connectedCallback() {
    const preview = this.dataset.preview === 'true';
    if (isAgeConfirmed() && !preview) {
      this.remove();
      return;
    }

    this.confirmBtn = this.querySelector('[data-age-gate-action="confirm"]');
    this.confirmBtn?.addEventListener('click', () => this.#confirm());

    document.body.setAttribute('data-age-gate-locked', '');
    if (typeof this.showPopover === 'function') {
      try {
        this.showPopover();
      } catch (e) {
        // Already open or browser doesn't support manual popover — ignore.
      }
    }

    this.confirmBtn?.focus({ preventScroll: true });
  }

  disconnectedCallback() {
    document.body.removeAttribute('data-age-gate-locked');
  }

  #confirm() {
    const days = Number(this.dataset.cookieDays || 30);
    writeCookie(COOKIE_NAME, '1', days);

    // Move focus out before closing so it can't be trapped in the top layer.
    const active = document.activeElement;
    if (active instanceof HTMLElement && this.contains(active)) {
      active.blur();
    }
    document.body.removeAttribute('data-age-gate-locked');
    document.dispatchEvent(new CustomEvent(EVENT_NAME));

    if (typeof this.hidePopover === 'function') {
      try {
        this.hidePopover();
      } catch (e) {
        // ignore
      }
    }
  }
}

if (!customElements.get('age-gate')) {
  customElements.define('age-gate', AgeGate);
}
